import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';
import { ColumnVisibilityMenu } from '../../table-common/ColumnVisibilityMenu';
import { DEFAULT_VISIBLE_ENVIRONMENT_COLUMNS } from './event-history-data';

const SORT_KEYS = {
  consist: 'CONSIST',
  car: 'CAR',
  priority: 'PRIORITY',
  timestamp: 'ACTIVATED',
  eventId: 'EVENT_ID',
  name: 'TITLE',
  action: 'ACTION',
};

const toSortingState = (sortType, sortReverse) => sortType
  ? [{ id: Object.keys(SORT_KEYS).find(key => SORT_KEYS[key] === sortType) || 'timestamp', desc: sortReverse }]
  : [];

const getEnvironmentKeys = (events, selectedEnvData, environmentColumns, supportsAlarmAnalytics) => {
  if (supportsAlarmAnalytics) return environmentColumns?.length ? environmentColumns : (selectedEnvData || []);
  const keys = [];
  (events || []).forEach(event => Object.keys(event.environmentData || {}).forEach(key => {
    if (!keys.includes(key)) keys.push(key);
  }));
  return keys;
};

const getEventColor = (event, usesQualifierEventColor) => (usesQualifierEventColor ? (event.qualifier || event.priority) : event.priority);

const toVisibilityKey = column => column.id || column.accessorKey;

const EventHistoryToolbar = ({ table, onExportExcel, onExportCsv, onResetColumns }) => {
  const [exportAnchor, setExportAnchor] = useState(null);

  return (
    <div className="event-history-table__toolbar-actions">
      <Tooltip title="Export">
        <IconButton
          size="small"
          onClick={event => setExportAnchor(event.currentTarget)}
          data-testid="event-history-export-button"
        >
          <SaveAltIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
        data-testid="event-history-export-menu"
      >
        <MenuItem onClick={() => { setExportAnchor(null); onExportExcel?.(); }} data-testid="event-history-export-excel">
          Export Excel
        </MenuItem>
        <MenuItem onClick={() => { setExportAnchor(null); onExportCsv?.(); }} data-testid="event-history-export-csv">
          Export CSV
        </MenuItem>
      </Menu>
      <ColumnVisibilityMenu table={table} testIdPrefix="event-history" onReset={onResetColumns} />
    </div>
  );
};

EventHistoryToolbar.propTypes = {
  table: PropTypes.object.isRequired,
  onExportExcel: PropTypes.func,
  onExportCsv: PropTypes.func,
  onResetColumns: PropTypes.func,
};

const buildColumns = ({ environmentKeys, usesAlternateEventDisplay, usesQualifierEventColor, showsActiveCarLabel }) => {
  const columns = [
    { accessorKey: 'consist', header: showsActiveCarLabel ? 'Active Car' : 'Consist', size: 90 },
    { accessorKey: 'car', header: usesAlternateEventDisplay ? 'Car Number' : 'Car', size: 80 },
  ];
  if (usesAlternateEventDisplay) columns.push({
    accessorKey: 'priority',
    header: 'Priority',
    size: 70,
    Cell: ({ row }) => {
      const color = getEventColor(row.original, usesQualifierEventColor);
      const icon = color === 'White' ? 'fa-circle' : 'fa-exclamation-triangle';
      const colorClass = color === 'Red' ? 'red-icon' : color === 'Yellow' ? 'yellow-icon' : '';
      return <i className={`fa ${icon} ${colorClass}`} />;
    },
  });
  columns.push(
    { accessorKey: 'timestamp', header: 'Timestamp', size: 140 },
    { accessorKey: 'eventId', header: usesAlternateEventDisplay ? 'Event Code' : 'Event ID', size: 90 },
    { accessorKey: 'name', header: usesAlternateEventDisplay ? 'Event Title' : 'Name', size: 160 },
    { accessorKey: 'action', header: usesAlternateEventDisplay ? 'Status' : 'Action', size: 90 },
    ...environmentKeys.map(key => ({ accessorKey: key, header: key, size: 100 })),
  );
  return columns.map(column => ({ ...column, enableSorting: Boolean(SORT_KEYS[column.accessorKey]) }));
};

export const EventHistoryTable = ({
  eventHistory = [],
  selectedEnvData = [],
  environmentColumns = [],
  loadBusy = false,
  hasMore = false,
  isActive = false,
  usesAlternateEventDisplay = false,
  usesQualifierEventColor = false,
  showsActiveCarLabel = false,
  supportsAlarmAnalytics = false,
  sortType = 'ACTIVATED',
  sortReverse = true,
  onLoadMore,
  onExportExcel,
  onExportCsv,
  formatTimestampFilter,
}) => {
  const environmentKeys = useMemo(
    () => getEnvironmentKeys(eventHistory, selectedEnvData, environmentColumns, supportsAlarmAnalytics),
    [eventHistory, selectedEnvData, environmentColumns, supportsAlarmAnalytics]
  );
  const columns = useMemo(
    () => buildColumns({ environmentKeys, usesAlternateEventDisplay, usesQualifierEventColor, showsActiveCarLabel }),
    [environmentKeys, usesAlternateEventDisplay, usesQualifierEventColor, showsActiveCarLabel]
  );
  const [sorting, setSorting] = useState(() => toSortingState(sortType, sortReverse));
  const [columnVisibilityOverrides, setColumnVisibilityOverrides] = useState({});
  const columnVisibility = useMemo(() => Object.fromEntries(
    columns.map(column => {
      const columnKey = toVisibilityKey(column);
      const isEnvironmentColumn = environmentKeys.includes(columnKey);
      const defaultVisible = !supportsAlarmAnalytics || !isEnvironmentColumn || DEFAULT_VISIBLE_ENVIRONMENT_COLUMNS.has(columnKey);
      return [columnKey, columnVisibilityOverrides[columnKey] ?? defaultVisible];
    })
  ), [columns, environmentKeys, columnVisibilityOverrides, supportsAlarmAnalytics]);

  useEffect(() => {
    setSorting(toSortingState(sortType, sortReverse));
  }, [sortType, sortReverse]);

  const rows = useMemo(() => eventHistory.map(event => {
    const row = {
      ...event,
      consist: event.consist,
      car: event.carNumber,
      timestamp: formatTimestampFilter(event.activated),
      eventId: usesAlternateEventDisplay ? event.eventHexCode : event.eventId,
      name: event.name || '-',
      action: event.action || '-',
      priority: getEventColor(event, usesQualifierEventColor),
    };
    environmentKeys.forEach(key => {
      row[key] = event.environmentData?.[key] || '-';
    });
    return row;
  }), [eventHistory, environmentKeys, usesAlternateEventDisplay, usesQualifierEventColor, formatTimestampFilter]);

  const handleSortingChange = updater => {
    const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (loadBusy) return;
    setSorting(nextSorting);
    const next = nextSorting[0];
    if (!next) return;
    onLoadMore?.({ reload: true, sortType: SORT_KEYS[next.id] || 'ACTIVATED', reversed: next.desc });
  };

  return (
    <MaterialDataTable
      className="event-history-container consist-event-history event-history-table--mrt"
      tableTestId="event-history-table-container"
      scrollContainerClassName="consist-event-history__scroll"
      fitViewport
      fillViewport
      ariaLabel="History event list"
      layoutMode="grid"
      columns={columns}
      data={rows}
      isLoading={loadBusy}
      enableSorting
      manualSorting
      sorting={sorting}
      onSortingChange={handleSortingChange}
      enableTopToolbar
      enableHiding
      showBuiltInColumnVisibility={false}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={updater => setColumnVisibilityOverrides(
        typeof updater === 'function' ? updater(columnVisibility) : updater
      )}
      toolbarInternalActions={({ table }) => (
        <EventHistoryToolbar table={table} onExportExcel={onExportExcel} onExportCsv={onExportCsv}
          onResetColumns={() => setColumnVisibilityOverrides({})} />
      )}
      onReachBottom={() => onLoadMore?.({
        reload: false,
        sortType,
        reversed: sortReverse,
      })}
      canLoadMore={isActive && hasMore}
      muiTableBodyRowProps={({ row }) => ({
        'data-testid': `event-history-row-${row.index}`,
        className: row.index % 2 ? 'row-odd' : 'row-even',
      })}
      muiTableBodyCellProps={({ row, column }) => ({
        'data-testid': `event-history-cell-${row.index}-${column.id}`,
      })}
    />
  );
};

EventHistoryTable.propTypes = {
  formatTimestampFilter: PropTypes.func.isRequired,
  eventHistory: PropTypes.array,
  selectedEnvData: PropTypes.array,
  environmentColumns: PropTypes.array,
  loadBusy: PropTypes.bool,
  hasMore: PropTypes.bool,
  isActive: PropTypes.bool,
  usesAlternateEventDisplay: PropTypes.bool,
  usesQualifierEventColor: PropTypes.bool,
  showsActiveCarLabel: PropTypes.bool,
  supportsAlarmAnalytics: PropTypes.bool,
  sortType: PropTypes.string,
  sortReverse: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onExportExcel: PropTypes.func,
  onExportCsv: PropTypes.func,
};
